import { EyeIcon, NoSymbolIcon } from '@heroicons/react/24/outline';

import { MarkdownMedia } from '@/components/markdown/MarkdownPreview';
import { OverflowText } from '@/components/OverflowText';
import { RiskBadge } from '@/components/RiskBadge';
import { Table } from '@/components/table/Table';
import { useActionHotkey } from '@/hooks/hotkeys';
import { AssetStatusComponent } from '@/sections/asset/components/badges';
import { WebpageSort } from '@/sections/webpages/hooks/useWebpageFilters';
import { getTypeBadgeLabel } from '@/sections/webpages/utils/badges';
import { Webpage } from '@/types';
import { formatDate } from '@/utils/date.util';

// Any focused interactive control that owns its own key activation. Bare-key
// hotkeys (v/o) and Enter must NOT fire their side effect while one of these is
// focused, so its native activation is preserved (codex 3567359392).
const INTERACTIVE_SELECTOR =
  'button, a[href], [role="button"], [role="tab"], [role="menuitem"], [role="link"], [contenteditable="true"]';
function isInteractiveControlFocused(): boolean {
  const el = document.activeElement;
  return el instanceof HTMLElement && el.closest(INTERACTIVE_SELECTOR) !== null;
}

interface WebpageTableProps {
  webpages: Webpage[];
  status: 'pending' | 'error' | 'success';
  error: Error | null;
  sort: WebpageSort;
  onSort: (sort: WebpageSort) => void;
  markViewed: (key: string) => void;
  isViewed: (key: string) => boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /**
   * Open the in-app Focus Review lightbox at this row's index (primary row
   * activation).
   */
  onOpenFocus: (index: number) => void;
  /** Controlled active-row index (the host's shared `activeIndex`, mirrors FocusReview). */
  activeIndex: number;
  /** Set the active-row index (J/K keyboard cursor + row click). */
  setActiveIndex: (index: number) => void;
  /**
   * Open the live site in a new tab. The host owns the single validated open
   * (protocol allowlist + `noopener,noreferrer`, security R1/R2) — this
   * component NEVER opens the URL itself.
   */
  onOpenLiveSite: (webpage: Webpage) => void;
  /**
   * Whether the Triage Inbox is the visible view. Gates the global J/K/V/O
   * hotkeys so they never fire while another view owns the screen. Defaults to
   * `true` (all hotkey handlers are optional-guarded, so an un-wired host is a
   * no-op regardless); the `global` scope is also auto-suppressed under an open
   * overlay (Focus Review), so J/K stay inert while the lightbox is up.
   */
  inboxVisible?: boolean;
  /**
   * Re-arm signature for the shared Table's `autoFillNextPage` (FIX B,
   * ENG-4416): a filter/sort signature the host owns. Its change resets the
   * auto-fill round counter to 0 (page arrivals must not). Threaded through
   * unchanged to `<Table autoFillResetKey>`; the host passes the SAME signature
   * used for the grid view so both views re-arm identically.
   */
  autoFillResetKey?: string;
}

export function WebpageTable({
  webpages,
  status,
  error,
  sort,
  onSort,
  markViewed,
  isViewed,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onOpenFocus,
  activeIndex,
  setActiveIndex,
  onOpenLiveSite,
  inboxVisible = true,
  autoFillResetKey,
}: WebpageTableProps) {
  const count = webpages.length;
  const activeWebpage = webpages[activeIndex];
  const activeKey = activeWebpage?.key;

  // J / K move the selection cursor with CLAMP (no wrap) — J at the last row
  // stays at the last row, K at row 0 stays at 0. (Contrast: the Focus Review
  // lightbox ←/→ WRAP modulo-N; the inbox J/K deliberately CLAMP.)
  const moveDown = () => {
    if (count === 0) return;
    setActiveIndex(Math.min(activeIndex + 1, count - 1));
  };
  const moveUp = () => {
    if (count === 0) return;
    setActiveIndex(Math.max(activeIndex - 1, 0));
  };
  const viewActive = () => {
    if (isInteractiveControlFocused()) return;
    if (activeKey) markViewed(activeKey);
  };
  const openActive = () => {
    if (isInteractiveControlFocused()) return;
    if (activeWebpage) onOpenLiveSite(activeWebpage);
  };
  // Bare Enter opens Focus Review for the active row — but ONLY when focus is
  // not on an interactive control. `useActionHotkey` defaults `preventDefault:
  // true` and fires it BEFORE the handler, so bailing inside the handler is not
  // enough to preserve a focused control's native Enter activation (it would
  // already be prevented). This binding therefore sets `preventDefault: false`
  // and calls `event.preventDefault()` itself ONLY on the branch that actually
  // opens the lightbox. react-hotkeys-hook only skips form tags
  // (input/textarea/select) — NOT buttons/links/tabs — so a Tab-focused
  // FilterDropdown trigger, ViewToggle tab, or sortable column header would
  // otherwise have its Enter hijacked (codex 3567359392). We detect any focused
  // interactive control via the shared `isInteractiveControlFocused` helper and
  // bail (no preventDefault) so its native Enter activation is preserved.
  const openActiveFocus = (event: KeyboardEvent) => {
    if (isInteractiveControlFocused()) {
      return; // focused control keeps its native Enter activation
    }
    if (activeWebpage) {
      event.preventDefault();
      markViewed(activeWebpage.key);
      onOpenFocus(activeIndex);
    }
  };

  // `scope: 'global'` (suppressed under an open overlay via HotkeyScopeSync, so
  // these go inert while Focus Review is up); `enabled: inboxVisible` keeps them
  // off when another view owns the screen. Input-field firing is off by default.
  useActionHotkey('j', moveDown, { scope: 'global', enabled: inboxVisible });
  useActionHotkey('k', moveUp, { scope: 'global', enabled: inboxVisible });
  useActionHotkey('v', viewActive, { scope: 'global', enabled: inboxVisible });
  useActionHotkey('o', openActive, { scope: 'global', enabled: inboxVisible });
  useActionHotkey('enter', openActiveFocus, {
    scope: 'global',
    enabled: inboxVisible,
    // The handler owns preventDefault (fired only on the open branch) so a
    // focused interactive control keeps its native Enter activation.
    preventDefault: false,
  });

  return (
    <Table<Webpage>
      name="webpages"
      data={webpages}
      status={status}
      error={error}
      rowHeight={200}
      selection={{ hide: true }}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      // FIX A (ENG-4390): scroll the active J/K cursor row into view. The host
      // owns the cursor (`activeIndex`); the shared Table translates it to a
      // groupedData position and calls virtualizer.scrollToIndex. Scroll-only —
      // highlight/aria-selected below are unchanged.
      scrollToRowIndex={activeIndex}
      // FIX B (ENG-4416): auto-page while the internal list is too short to
      // overflow under a client-side filter (e.g. "Not Viewed"). Same dead-zone
      // the grid has; capped, with a "Load more" fallback inside the Table.
      autoFillNextPage
      autoFillResetKey={autoFillResetKey}
      sort={{
        orderBy: sort.orderBy,
        descending: sort.descending,
        onSort: onSort,
      }}
      // Row activation is the in-app Focus Review (NOT navigate-away). The
      // legacy navigate-away sink is removed from this file entirely — the host
      // owns the single validated new-tab open via `onOpenLiveSite`
      // (security R1/R2/R8).
      onRowClick={(webpage: Webpage, rowIndex: number) => {
        setActiveIndex(rowIndex);
        markViewed(webpage.key);
        onOpenFocus(rowIndex);
      }}
      // Active-row affordance: brand-tertiary wash + inset brand ring on the J/K
      // cursor row (design-spec §5.2), matched by webpage key. Scroll-into-view
      // for the cursor is now driven by the `scrollToRowIndex` prop above; the
      // highlight + aria-selected below are the visual state the host owns.
      rowClassName={(webpage: Webpage) =>
        activeKey !== undefined && webpage.key === activeKey
          ? 'bg-chariot-brand-tertiary ring-chariot-brand-primary ring-2 ring-inset'
          : ''
      }
      getRowProps={(webpage: Webpage) => ({
        'aria-selected': activeKey !== undefined && webpage.key === activeKey,
      })}
      columns={[
        {
          label: 'Screenshot',
          id: 'screenshot',
          fixedWidth: 264,
          sorting: false,
          cell: (webpage: Webpage) => {
            if (!webpage.screenshot)
              return (
                <div className="bg-chariot-background-secondary flex h-44 w-full items-center justify-center overflow-hidden rounded">
                  <NoSymbolIcon className="text-chariot-text-disabled size-6" />
                </div>
              );

            return (
              // thumbnail={false} drops the dead nested role=button (a11y). The
              // parent-scoped img utilities fill + crop the fixed cell (the plain
              // img ships only `max-w-full rounded-lg` on its own).
              <div className="flex h-44 w-full items-center justify-center overflow-hidden rounded [&_img]:h-full [&_img]:w-full [&_img]:object-cover">
                <MarkdownMedia src={webpage.screenshot} thumbnail={false} />
              </div>
            );
          },
        },
        {
          label: 'URL',
          id: 'url',
          fixedWidth: 300,
          copy: true,
          cell: (webpage: Webpage) => <OverflowText text={webpage.url} />,
        },
        {
          label: 'Type',
          id: 'type',
          fixedWidth: 60,
          sorting: false,
          cell: (webpage: Webpage) =>
            getTypeBadgeLabel(webpage.type) ?? (webpage.type || '-'),
        },
        {
          label: 'Service',
          id: 'service',
          fixedWidth: 60,
          sorting: false,
          cell: (webpage: Webpage) => (
            <span className="capitalize">{webpage.service || '-'}</span>
          ),
        },
        {
          label: 'Risk',
          // A real server-side ordering (ENG-4395): header clicks emit
          // `orderBy: 'riskScore'`, mapped to the backend `risk_score` inside
          // useWebpages. Sorting defaults ON (like `url`/`visited`).
          id: 'riskScore',
          fixedWidth: 90,
          align: 'center' as const,
          cell: (webpage: Webpage) => (
            // The score comes straight off the Webpage node (ENG-4395). An
            // absent riskScore (never cron-stamped) renders the neutral "—"
            // chip — never floored to a fake tier-1.
            <RiskBadge score={webpage.riskScore} />
          ),
        },
        {
          label: 'Status',
          id: 'status',
          fixedWidth: 75,
          sorting: false,
          cell: (webpage: Webpage) => <AssetStatusComponent asset={webpage} />,
        },
        {
          label: 'Viewed',
          // Synthetic column id (not a real Webpage field). `sorting: false`, so
          // the id is only a stable column identity — the cast bridges the
          // `keyof Webpage` column-id type without pointing at a misleading field.
          id: 'viewed' as keyof Webpage,
          fixedWidth: 60,
          sorting: false,
          align: 'center' as const,
          cell: (webpage: Webpage) => {
            if (!isViewed(webpage.key)) return null;
            return <EyeIcon className="text-chariot-text-disabled size-4" />;
          },
        },
        {
          label: 'Last Seen',
          id: 'visited',
          fixedWidth: 75,
          cell: (webpage: Webpage) => (
            <span className="text-chariot-text-secondary">
              {formatDate(webpage.visited)}
            </span>
          ),
        },
      ]}
    />
  );
}
